import React from "react";
import ShareButton from "../ShareButton/ShareButton";
import AddToPlanButton from "../AddToPlanButton/AddToPlanButton";
import {
  formatTagLabel,
  normalizeTags,
  summaryBulletLines,
} from "../../lib/cardDisplay";
import { buildActivityCardShareUrl } from "../../lib/activityShareDeepLink";
import {
  buildEventCardShareMessage,
  buildStoryCardShareMessage,
} from "../../lib/whatsAppShare";
import { formatActivityWhenDeCh } from "../../lib/activityWhenDisplay";

const renderWithLineBreaks = (text) => {
  if (!text) return null;
  const parts = text.split("\\n");
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

function BriefRow({ label, value }) {
  if (!value || !String(value).trim()) return null;
  return (
    <div className="parent-brief-row">
      <span className="parent-brief-dt">{label}</span>
      <span className="parent-brief-dd">{renderWithLineBreaks(String(value).trim())}</span>
    </div>
  );
}

export default function CardContent({
  data,
  isStory,
  columnIndex,
  columnId: columnIdProp,
  index,
  onAddToSlot,
  listLabel = "",
  planDayDates = null,
  planSlots = null,
  getSlotKey = null,
}) {
  const columnId =
    columnIdProp != null && columnIdProp !== "" ? String(columnIdProp) : String(columnIndex);
  const eventId = data?.id ?? index;
  const shareUrl = buildActivityCardShareUrl({
    origin: window.location.origin,
    columnId,
    eventId,
  });
  const storyWhatsAppMessage = buildStoryCardShareMessage({
    title: data?.title,
    url: shareUrl,
    location: data?.location,
  });
  const eventWhatsAppMessage = buildEventCardShareMessage({
    title: data?.title,
    url: shareUrl,
    location: data?.location,
  });

  const tagKeys = normalizeTags(data?.tags);
  const bullets = summaryBulletLines(data?.summary);
  const fallbackBrief =
    bullets.length === 0 && !isStory
      ? [
          formatActivityWhenDeCh(data?.starts_at, data?.ends_at),
          data?.duration_hint,
          data?.age_hint,
          data?.location,
        ]
          .map((x) => (x != null && String(x).trim() ? String(x).trim() : ""))
          .filter(Boolean)
          .join(" · ")
      : null;

  if (isStory) {
    return (
      <>
        <div className="parent-brief">
          {listLabel ? (
            <p className="parent-brief-eyebrow">{listLabel}</p>
          ) : null}
          <div className="story-card parent-brief-story">
            <h3 className="story-title">{renderWithLineBreaks(data.title)}</h3>
            {bullets.length > 0 ? (
              <section className="parent-brief-section">
                <h4 className="parent-brief-heading">In Kürze</h4>
                <ul className="parent-brief-bullets">
                  {bullets.map((line, bi) => (
                    <li key={`${bi}-${line.slice(0, 24)}`}>{line}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            <section className="parent-brief-section">
              <h4 className="parent-brief-heading">So geht&apos;s</h4>
              <p className="story-line">{renderWithLineBreaks(data.storyline)}</p>
            </section>
            {data.truncated ? (
              <div className="story-more">… (gekürzt für die Vorschau)</div>
            ) : null}
          </div>
        </div>
        <div className="button-row">
          <ShareButton
            whatsappMessage={storyWhatsAppMessage}
            scenario="event_story"
            shareUrl={shareUrl}
          />
          {onAddToSlot && (
            <AddToPlanButton
              columnId={columnId}
              eventId={eventId}
              onAddToSlot={onAddToSlot}
              activity={data}
              planDayDates={planDayDates}
              planSlots={planSlots}
              getSlotKey={getSlotKey}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <article className="parent-brief">
        {listLabel ? <p className="parent-brief-eyebrow">{listLabel}</p> : null}

        {data.image ? (
          <img src={data.image} alt={data.title} className="event-img parent-brief-hero" />
        ) : null}

        <h3 className="parent-brief-title">{renderWithLineBreaks(data.title)}</h3>

        {(bullets.length > 0 || fallbackBrief) && (
          <section className="parent-brief-section">
            <h4 className="parent-brief-heading">In Kürze</h4>
            {bullets.length > 0 ? (
              <ul className="parent-brief-bullets">
                {bullets.map((line, bi) => (
                  <li key={`${bi}-${line.slice(0, 24)}`}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="parent-brief-fallback">{fallbackBrief}</p>
            )}
          </section>
        )}

        {tagKeys.length > 0 ? (
          <div className="parent-brief-tags" aria-label="Stichworte">
            {tagKeys.map((k) => (
              <span key={k} className="parent-brief-tag-chip">
                {formatTagLabel(k)}
              </span>
            ))}
          </div>
        ) : null}

        {(() => {
          const whenVal = formatActivityWhenDeCh(data?.starts_at, data?.ends_at);
          const hasInfos =
            whenVal ||
            (data.location && String(data.location).trim()) ||
            (data.duration_hint && String(data.duration_hint).trim()) ||
            (data.age_hint && String(data.age_hint).trim()) ||
            (data.price_hint && String(data.price_hint).trim()) ||
            (data.booking_hint && String(data.booking_hint).trim());
          if (!hasInfos) return null;
          return (
            <section className="parent-brief-section">
              <h4 className="parent-brief-heading">Infos für euren Besuch</h4>
              <div className="parent-brief-infos">
                <BriefRow label="Wann" value={whenVal} />
                <BriefRow label="Wo" value={data.location} />
                <BriefRow label="Dauer / Aufwand" value={data.duration_hint} />
                <BriefRow label="Alter" value={data.age_hint} />
                <BriefRow label="Kosten" value={data.price_hint} />
                <BriefRow label="Reservation" value={data.booking_hint} />
              </div>
            </section>
          );
        })()}

        {data.link ? (
          <a
            href={data.link}
            target="_blank"
            rel="noopener noreferrer"
            className="event-brief-cta"
            onClick={(e) => e.stopPropagation()}
          >
            Website öffnen
          </a>
        ) : null}
      </article>

      <div className="button-row">
        <ShareButton
          whatsappMessage={eventWhatsAppMessage}
          scenario="event"
          shareUrl={shareUrl}
        />
        {onAddToSlot && (
          <AddToPlanButton
            columnId={columnId}
            eventId={eventId}
            onAddToSlot={onAddToSlot}
            activity={data}
            planDayDates={planDayDates}
            planSlots={planSlots}
            getSlotKey={getSlotKey}
          />
        )}
      </div>
    </>
  );
}
